import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPinIcon, BriefcaseIcon, BanknoteIcon, LockIcon, ClockIcon, ChevronLeftIcon, CheckIcon, ShareIcon, BookmarkIcon } from 'lucide-react';
import { jobs, formatPosted } from '../lib/data';
import { Button } from '../components/ui/Button';
import { ApplyModal } from '../components/jobs/ApplyModal';
import { JobCard } from '../components/jobs/JobCard';
export function JobDetail() {
  const {
    id
  } = useParams();
  const job = jobs.find(j => j.id === id);
  const [applyOpen, setApplyOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!job) {
    return <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-id="element-515" data-mp="2v8vd7l22oowu">
        <h1 className="font-serif text-3xl text-ink" data-id="element-516" data-mp="2v8lmtwd48fqm">Opportunity not found</h1>
        <p className="mt-3 text-ink-600" data-id="element-517" data-mp="1nw33e4vlatsl">This mandate may have been filled or withdrawn.</p>
        <Button to="/opportunities" variant="primary" size="md" className="mt-8" data-id="element-518" data-mp="1jsn5743vglds">
          Browse opportunities
        </Button>
      </div>;
  }
  const related = jobs.filter(j => j.id !== job.id && j.discipline === job.discipline).slice(0, 2);
  const facts = [{
    icon: MapPinIcon,
    label: 'Location',
    value: job.location
  }, {
    icon: BriefcaseIcon,
    label: 'Arrangement',
    value: job.remote
  }, {
    icon: BanknoteIcon,
    label: 'Compensation',
    value: job.compensation
  }, {
    icon: ClockIcon,
    label: 'Posted',
    value: formatPosted(job.postedDays)
  }];
  return <div className="bg-ivory-100" data-id="element-519" data-mp="mq9iol3xwcza">
      <div className="border-b border-ink/10 bg-ink text-ivory-200" data-id="element-520" data-mp="mrawgxccbdkp">
        <div className="mx-auto max-w-5xl px-6 py-12 lg:px-10 lg:py-16" data-id="element-521" data-mp="824zzh4w01gi">
          <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-sm text-ink-400 transition-colors hover:text-ivory-100" data-id="element-522" data-mp="czx0gzjlkvwb">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-523" data-mp="3v85r5pizhrnp" /> All opportunities
          </Link>

          <div className="mt-8 flex flex-col justify-between gap-6 sm:flex-row sm:items-start" data-id="element-524" data-mp="41l1oa8kurss">
            <div data-id="element-525" data-mp="25qqy142qpzum">
              <div className="flex flex-wrap items-center gap-2" data-id="element-526" data-mp="20xxu2if3ivjl">
                <span className="text-xs font-medium uppercase tracking-widest2 text-gold" data-id="element-527" data-mp="20we9wgqbk0cj">
                  {job.type}
                </span>
                {job.confidential && <span className="inline-flex items-center gap-1 rounded-full bg-ivory-100/10 px-2.5 py-1 text-[10px] font-medium text-ivory-200" data-id="element-528" data-mp="1rykgbdjxvkt2">
                    <LockIcon className="h-2.5 w-2.5" data-id="element-529" data-mp="2jofiiaswb4z5" /> Confidential
                  </span>}
              </div>
              <h1 className="mt-4 font-serif text-3xl leading-tight text-ivory-100 sm:text-4xl lg:text-5xl" data-id="element-530" data-mp="2fm2djolz3b3f">
                {job.title}
              </h1>
              <p className="mt-3 text-lg text-ink-400" data-id="element-531" data-mp="2aq3jbkdyqyv3">
                {job.confidential ? 'Confidential Client' : job.company}
              </p>
            </div>
            <div className="flex gap-3" data-id="element-532" data-mp="z7srx29480ga">
              <button onClick={() => setSaved(s => !s)} aria-pressed={saved} className="flex h-11 w-11 items-center justify-center rounded-full border border-ivory-200/25 text-ivory-200 transition-colors hover:border-gold hover:text-gold" aria-label={saved ? 'Saved' : 'Save role'} data-id="element-533" data-mp="3avxkgf6uxbo8">
                <BookmarkIcon className={`h-5 w-5 ${saved ? 'fill-gold text-gold' : ''}`} data-id="element-534" data-mp="3oowy0bn2wvt5" />
              </button>
              <button className="flex h-11 w-11 items-center justify-center rounded-full border border-ivory-200/25 text-ivory-200 transition-colors hover:border-gold hover:text-gold" aria-label="Share role" data-id="element-535" data-mp="3tojceoen8i3j">
                <ShareIcon className="h-5 w-5" data-id="element-536" data-mp="bh3l2ebvz9k9" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-10 px-6 py-12 lg:grid-cols-3 lg:px-10 lg:py-16" data-id="element-537" data-mp="2986cz6rn9qry">
        <div className="lg:col-span-2" data-id="element-538" data-mp="2d8p1ory8qgpx">
          <section data-id="element-539" data-mp="1hxqn79v3oiw6">
            <h2 className="font-serif text-2xl text-ink" data-id="element-540" data-mp="1hx8v8pfh0qu2">About the mandate</h2>
            <p className="mt-4 text-base leading-relaxed text-ink-600" data-id="element-541" data-mp="1dwoxbjn2pj6v">{job.summary}</p>
            {!job.confidential && <p className="mt-4 text-base leading-relaxed text-ink-600" data-id="element-542" data-mp="1rwg4b0f69zbd">
                {job.companyBlurb}
              </p>}
          </section>

          <section className="mt-10" data-id="element-543" data-mp="z7ior52cigmb">
            <h2 className="font-serif text-2xl text-ink" data-id="element-544" data-mp="z8kd2b8dgzlz">The role</h2>
            <ul className="mt-5 space-y-3" data-id="element-545" data-mp="u8pls6koq80e">
              {job.responsibilities.map(r => <li key={r} className="flex gap-3 text-base leading-relaxed text-ink-600" data-id="element-546" data-mp="18xc32dtvh9u9">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold-dark" data-id="element-547" data-mp="146lvqv1vywwx" />
                  {r}
                </li>)}
            </ul>
          </section>

          <section className="mt-10" data-id="element-548" data-mp="gf6r2vmn03ii">
            <h2 className="font-serif text-2xl text-ink" data-id="element-549" data-mp="bi7tu47ofgg3">What we look for</h2>
            <ul className="mt-5 space-y-3" data-id="element-550" data-mp="bg3snu15wvji">
              {job.requirements.map(r => <li key={r} className="flex gap-3 text-base leading-relaxed text-ink-600" data-id="element-551" data-mp="2n373wkraoo1a">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold-dark" data-id="element-552" data-mp="2ry35abif8nr2" />
                  {r}
                </li>)}
            </ul>
          </section>

          <div className="mt-10 rounded-2xl border border-ink/10 bg-ivory-50 p-6" data-id="element-553" data-mp="2i705re554gtw">
            <p className="text-sm leading-relaxed text-ink-600" data-id="element-554" data-mp="35wuh1r9t9xc1">
              This is a partner-led, confidential search. All approaches are handled with
              the utmost discretion.
            </p>
          </div>
        </div>

        <aside className="lg:col-span-1" data-id="element-555" data-mp="2wuth2mn70x6o">
          <div className="sticky top-24 rounded-2xl border border-ink/10 bg-ivory-50 p-6 shadow-soft" data-id="element-556" data-mp="31tocco2cke7o">
            <dl className="space-y-5" data-id="element-557" data-mp="1lfelnnamuddo">
              {facts.map(fact => <div key={fact.label} className="flex items-start gap-3" data-id="element-558" data-mp="1qf0yvwqgntmh">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink/5 text-gold-dark" data-id="element-559" data-mp="1qcy3ex6jh8ad">
                    <fact.icon className="h-4 w-4" data-id="element-560" data-mp="3n53u3h7rlflm" />
                  </span>
                  <div data-id="element-561" data-mp="3n76osejv3wwo">
                    <dt className="text-xs uppercase tracking-wide text-ink-500" data-id="element-562" data-mp="3ie57y103hnag">
                      {fact.label}
                    </dt>
                    <dd className="mt-0.5 text-sm font-medium text-ink" data-id="element-563" data-mp="3ec0ilegojctm">{fact.value}</dd>
                  </div>
                </div>)}
            </dl>
            <Button onClick={() => setApplyOpen(true)} variant="gold" size="lg" className="mt-6 w-full" data-id="element-564" data-mp="34e2uqs2qfi1j">
              Express interest
            </Button>
            <p className="mt-3 text-center text-xs text-ink-500" data-id="element-565" data-mp="2vez7dbwj8gh8">
              Your interest is shared only with the search team.
            </p>
          </div>
        </aside>
      </div>

      {related.length > 0 && <div className="mx-auto max-w-5xl px-6 pb-20 lg:px-10" data-id="element-566" data-mp="tncf770wmfj6">
          <h2 className="font-serif text-2xl text-ink" data-id="element-567" data-mp="2bkzazjitbhlq">Related mandates</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-568" data-mp="2gj0w09zstqzu">
            {related.map(r => <JobCard key={r.id} job={r} data-id="element-569" data-mp="2la93v1lhd4aw" />)}
          </div>
        </div>}

      <ApplyModal job={job} open={applyOpen} onClose={() => setApplyOpen(false)} data-id="element-570" data-mp="2pbkv1bk05xl7" />
    </div>;
}